import React, {useEffect, useState} from 'react';
import {Form} from 'react-final-form';
import {useIntl} from 'react-intl';
import cn from 'classnames';

import {EEventSource} from 'coreClasses/events/IEvents';
import {useCustomerMgr, useLocation, useNotify} from 'coreHooks';
import validationRules from 'coreUtils/validationRules';

import {useGeneralProvider} from 'brandComponents/_app-config/GeneralProvider';
import Button from 'brandComponents/Button';
import CheckboxField from 'brandComponents/Form/Subcomponents/CheckboxField';
import Field from 'brandComponents/Form/Subcomponents/Field';
import Link from 'brandComponents/Link';
import {NEWSLETTER_SOURCE} from 'brandConstants';

import {INewsletterPopupFormProps, IRegistrationValidationErrors} from './INewsletterPopupForm';
import messages from './messages';

import styles from './NewsletterPopupForm.module.scss';

const NewsletterPopupForm = ({classNames}: INewsletterPopupFormProps) => {
	const intl = useIntl();
	const {managerRepository} = useGeneralProvider();
	const customerMgr = useCustomerMgr();
	const notify = useNotify();
	const [isSubscribedOnLocation, setIsSubscribedOnLocation] = useState();
	const nlSubscriptionSrc: string = EEventSource[EEventSource.NEWSLETTER_POPUP];
	const [loading, setLoading] = useState(false);
	const location = useLocation();

	const initialValues = {
		email: '',
		privacy: false,
		profiling: false,
	};

	const handleSubscribe = async (value: Record<string, any>) => {
		const {gender, email, privacy, profiling} = value;

		const newsletterSource = {
			newsletterSource: NEWSLETTER_SOURCE[nlSubscriptionSrc],
			newsletterSourceDetails: {
				pageType: NEWSLETTER_SOURCE.CUSTOM_POPUP,
			}
		};

		try {
			const {subscriptionCode} = await customerMgr.subscribeToNewsletter(
				email,
				gender,
				EEventSource.NEWSLETTER_POPUP,
				newsletterSource,
				[],
				profiling,
				privacy,
			);

			switch (subscriptionCode) {
				case 'OK':
					notify.success(intl.formatMessage(messages.signInSuccess));

					break;
				default:
					notify.error(intl.formatMessage(messages.signInFailure));
			}
		} catch {
			notify.error(intl.formatMessage(messages.signInFailure));
		}

		setLoading(false);

		managerRepository.getPopupsMgr().closePopup('newsletter');
	};

	useEffect(() => {
		if (isSubscribedOnLocation && location.pathname !== isSubscribedOnLocation) {
			setIsSubscribedOnLocation(null);
		}
	}, [location.pathname]);

	return (
		<Form
			onSubmit={handleSubscribe}
			initialValues={initialValues}
			validate={(values) => {
				const validationErrors: IRegistrationValidationErrors = {};

				if (!values.email) {
					validationErrors.email = intl.formatMessage(messages.signInEmailError);
				}

				if (!values?.privacy) {
					validationErrors.privacy = intl.formatMessage(messages.mandatory);
				}

				return validationErrors;
			}}
			render={({handleSubmit, hasValidationErrors}) => {
				return (
					<form className={cn(classNames, styles['newsletter-popup__form'])} onSubmit={handleSubmit}>
						<Field
							name="email"
							type="email"
							id="newsletter-email"
							component="input"
							requirednessLabel="*"
							label={intl.formatMessage(messages.signInEmailPlaceholder)}
							validation={[
								{
									rule: validationRules.isNotEmpty,
									errorMessage: intl.formatMessage(messages.signInEmailError),
								},
								{
									rule: validationRules.isEmail,
									errorMessage: intl.formatMessage(messages.invalidEmail),
								},
							]}
						/>
						<div className={styles['newsletter-popup__privacy-policy-container']}>
							<CheckboxField
								name="privacy"
								label={intl.formatMessage(messages.signInPrivacyPolicy, {
									privacy: (chunk) => <Link to="/privacy-policy">{chunk}</Link>,
								})}
								classNames={styles['newsletter-popup__privacy-policy']}
							/>

							<CheckboxField name="profiling" label={intl.formatMessage(messages.checkboxPrivacy)} />
						</div>
						<Button
							type="submit"
							classNames={styles['newsletter-popup__cta']}
							variant="primary"
							loading={loading}
							onClick={!hasValidationErrors ? handleSubmit : undefined}
						>
							{intl.formatMessage(messages.signInCtaAfterClick)}
						</Button>
					</form>
				);
			}}
		/>
	);
};

export default NewsletterPopupForm;
